import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import AdminWidgets from "@/components/AdminWidgets";
import { Link } from "react-router-dom";
import {
    Clock,
    ArrowRight,
    Receipt,
    AirplaneTilt,
    ShieldCheck,
    Briefcase,
    Warning,
} from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toFixed(2);

const Stat = ({ label, value, sub, mono = true, testId }) => (
    <div
        className="border border-slate-200 bg-white p-6 flex flex-col gap-2"
        data-testid={testId}
    >
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            {label}
        </div>
        <div
            className={`text-3xl font-bold text-slate-950 ${
                mono ? "font-mono tabular-nums" : "font-display"
            }`}
        >
            {value}
        </div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
);

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        http.get("/dashboard/me").then((r) => setData(r.data));
    }, []);

    if (!user) return null;

    const isAdmin = user.role === "admin";
    const today = new Date();
    const greeting =
        today.getHours() < 13
            ? "Buenos días"
            : today.getHours() < 20
              ? "Buenas tardes"
              : "Buenas noches";

    const target = data?.weekly_target || 40;
    const pct = data ? Math.min(100, (data.hours_week / target) * 100) : 0;

    return (
        <div data-testid="page-dashboard">
            <PageHeader
                eyebrow="Resumen"
                title={`${greeting}, ${user.name?.split(" ")[0] || ""}.`}
                description="Esto es lo que pasa hoy en tu jornada."
                testId="dashboard-header"
            />

            <div className="p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                        <Stat
                            label="Hoy"
                            value={`${fmt(data?.hours_today)} h`}
                            sub={today.toLocaleDateString("es-ES", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                            })}
                            testId="stat-hours-today"
                        />
                        <Stat
                            label="Esta semana"
                            value={`${fmt(data?.hours_week)} h`}
                            sub={`Objetivo ${target} h`}
                            testId="stat-hours-week"
                        />
                        <Stat
                            label="Vacaciones"
                            value={`${data?.vacation_remaining ?? 0} d`}
                            sub={`${data?.vacation_used ?? 0} usados de ${data?.vacation_total ?? 0}`}
                            testId="stat-vacation"
                        />
                        <Stat
                            label={isAdmin ? "Pendientes (admin)" : "Mis solicitudes"}
                            value={
                                isAdmin
                                    ? data?.pending_admin_requests ?? 0
                                    : data?.pending_my_requests ?? 0
                            }
                            sub={isAdmin ? "Por aprobar" : "En espera"}
                            mono={false}
                            testId="stat-pending"
                        />
                    </div>

                    <div className="border border-slate-200 bg-white">
                        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Avance semanal
                                </div>
                                <div className="font-display text-xl font-semibold mt-1">
                                    {fmt(data?.hours_week)} / {target} horas
                                </div>
                            </div>
                            <Link
                                to="/timesheet"
                                className="text-sm text-[#002BAF] flex items-center gap-1 hover:gap-2 transition-all"
                                data-testid="dashboard-go-timesheet"
                            >
                                Abrir parte de horas <ArrowRight size={14} />
                            </Link>
                        </div>
                        <div className="px-6 py-6">
                            <div className="h-2 bg-slate-100 relative">
                                <div
                                    className="absolute inset-y-0 left-0 bg-[#002BAF]"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <div className="mt-3 text-xs text-slate-500 font-mono">
                                {pct.toFixed(0)}% completado
                            </div>
                        </div>
                    </div>

                    <ProjectsProgressWidget isAdmin={isAdmin} />
                </div>

                <div className="lg:col-span-4 space-y-6">
                    <Link
                        to="/timesheet"
                        className="block border border-slate-200 bg-white p-6 hover:border-slate-400 transition-colors"
                        data-testid="quick-action-timesheet"
                    >
                        <Clock size={22} className="text-[#002BAF]" />
                        <div className="font-display text-lg font-semibold mt-3">
                            Registrar horas
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                            Imputa horas a proyectos por día y semana.
                        </div>
                    </Link>
                    <Link
                        to="/absences"
                        className="block border border-slate-200 bg-white p-6 hover:border-slate-400 transition-colors"
                        data-testid="quick-action-absences"
                    >
                        <AirplaneTilt size={22} className="text-[#002BAF]" />
                        <div className="font-display text-lg font-semibold mt-3">
                            Pedir vacaciones
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                            Crea una solicitud y gestiona tus ausencias.
                        </div>
                    </Link>
                    <Link
                        to="/expenses"
                        className="block border border-slate-200 bg-white p-6 hover:border-slate-400 transition-colors"
                        data-testid="quick-action-expenses"
                    >
                        <Receipt size={22} className="text-[#002BAF]" />
                        <div className="font-display text-lg font-semibold mt-3">
                            Registrar gastos
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                            Dietas, desplazamientos y otros gastos por proyecto.
                        </div>
                    </Link>
                    {isAdmin && (
                        <Link
                            to="/admin/approvals"
                            className="block border border-slate-200 bg-white p-6 hover:border-slate-400 transition-colors"
                            data-testid="quick-action-approvals"
                        >
                            <ShieldCheck size={22} className="text-[#002BAF]" />
                            <div className="font-display text-lg font-semibold mt-3">
                                Bandeja de aprobación
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                                Revisa solicitudes pendientes del equipo.
                            </div>
                        </Link>
                    )}
                </div>
            </div>
            {isAdmin && (
                <div className="px-6 lg:px-10 pb-10">
                    <AdminWidgets />
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Projects progress widget                                            */
/* ------------------------------------------------------------------ */

function fmtEuro(n) {
    return (
        (Number(n) || 0).toLocaleString("es-ES", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }) + " €"
    );
}

function fmtDate(iso) {
    if (!iso) return null;
    try {
        const d = new Date(`${iso}T00:00:00`);
        return d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch (_) {
        return iso;
    }
}

function ProjectsProgressWidget({ isAdmin }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | over | at-risk | ok
    const [yearFilter, setYearFilter] = useState("all");
    const [limit, setLimit] = useState(15);

    const load = (year) => {
        setBusy(true);
        const params = year && year !== "all" ? { year } : {};
        http.get("/dashboard/projects-progress", { params })
            .then((r) => setData(r.data))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (!isAdmin) return;
        load(yearFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yearFilter, isAdmin]);

    const rows = useMemo(() => {
        if (!data?.projects) return [];
        const q = search.trim().toLowerCase();
        return data.projects.filter((p) => {
            if (q) {
                const hay =
                    p.project_name.toLowerCase().includes(q) ||
                    (p.project_code || "").toLowerCase().includes(q) ||
                    p.customer_name.toLowerCase().includes(q);
                if (!hay) return false;
            }
            if (statusFilter === "over" && !p.is_over_budget) return false;
            if (
                statusFilter === "at-risk" &&
                !(p.progress_pct != null && p.progress_pct >= 75 && p.progress_pct <= 100)
            )
                return false;
            if (
                statusFilter === "ok" &&
                !(p.progress_pct == null || p.progress_pct < 75)
            )
                return false;
            return true;
        });
    }, [data, search, statusFilter]);

    const counters = useMemo(() => {
        if (!data?.projects) return { total: 0, over: 0, atRisk: 0 };
        return data.projects.reduce(
            (acc, p) => {
                acc.total += 1;
                if (p.is_over_budget) acc.over += 1;
                else if (
                    p.progress_pct != null &&
                    p.progress_pct >= 75 &&
                    p.progress_pct <= 100
                )
                    acc.atRisk += 1;
                return acc;
            },
            { total: 0, over: 0, atRisk: 0 },
        );
    }, [data]);

    const projAccessors = useMemo(
        () => ({
            project: (p) => (p.project_name || "").toLowerCase(),
            consumido: (p) => Number(p.spent_cost || 0),
            presupuesto: (p) => Number(p.planned_budget || 0),
            avance: (p) => (p.progress_pct == null ? null : Number(p.progress_pct)),
            fin: (p) => p.estimated_end || "",
        }),
        [],
    );
    const {
        sorted: sortedRows,
        sortKey,
        sortDir,
        headerProps,
    } = useSortableTable(rows, "dashboard-projects-progress", projAccessors, {
        initialKey: "avance",
        initialDir: "desc",
    });

    if (!isAdmin) return null;

    return (
        <div
            className="border border-slate-200 bg-white"
            data-testid="projects-progress-widget"
        >
            <div className="px-6 py-5 border-b border-slate-200 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Estado de proyectos
                    </div>
                    <div className="font-display text-xl font-semibold mt-1 flex items-center gap-2">
                        <Briefcase size={18} className="text-[#002BAF]" />
                        Presupuesto y fecha estimada de fin
                    </div>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                    <span className="px-2 py-1 rounded-sm bg-slate-100 text-slate-700 font-mono">
                        {counters.total} proyectos
                    </span>
                    {counters.over > 0 && (
                        <button
                            onClick={() =>
                                setStatusFilter(statusFilter === "over" ? "all" : "over")
                            }
                            className={`px-2 py-1 rounded-sm font-mono border transition-colors ${
                                statusFilter === "over"
                                    ? "bg-rose-600 text-white border-rose-600"
                                    : "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100"
                            }`}
                            data-testid="proj-filter-over"
                        >
                            {counters.over} sobre presupuesto
                        </button>
                    )}
                    {counters.atRisk > 0 && (
                        <button
                            onClick={() =>
                                setStatusFilter(
                                    statusFilter === "at-risk" ? "all" : "at-risk",
                                )
                            }
                            className={`px-2 py-1 rounded-sm font-mono border transition-colors ${
                                statusFilter === "at-risk"
                                    ? "bg-amber-500 text-white border-amber-500"
                                    : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                            }`}
                            data-testid="proj-filter-risk"
                        >
                            {counters.atRisk} en riesgo
                        </button>
                    )}
                </div>
            </div>
            <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar proyecto, código o cliente..."
                    className="flex-1 min-w-[220px] border border-slate-300 rounded-sm px-3 py-1.5 text-sm outline-none focus:border-slate-500"
                    data-testid="proj-progress-search"
                />
                <select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs bg-white"
                    data-testid="proj-progress-year"
                >
                    <option value="all">Todos los años</option>
                    {(data?.available_years || []).map((y) => (
                        <option key={y} value={String(y)}>
                            {y}
                        </option>
                    ))}
                </select>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs bg-white"
                    data-testid="proj-progress-status"
                >
                    <option value="all">Todos</option>
                    <option value="over">Sobre presupuesto</option>
                    <option value="at-risk">En riesgo (≥75%)</option>
                    <option value="ok">A tiempo (&lt;75%)</option>
                </select>
                <span className="text-[11px] text-slate-500 font-mono">
                    {rows.length} de {data?.projects?.length || 0}
                </span>
            </div>
            {busy ? (
                <div className="text-center py-12 text-sm text-slate-500">
                    Cargando…
                </div>
            ) : rows.length === 0 ? (
                <div
                    className="text-center py-14 text-sm text-slate-500"
                    data-testid="proj-progress-empty"
                >
                    <Briefcase
                        size={28}
                        className="mx-auto text-slate-300 mb-2"
                    />
                    No hay proyectos que mostrar.
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200 bg-slate-50">
                                    <th {...headerProps("project", "text-left py-2 px-4")}>
                                        Proyecto
                                        <SortIndicator
                                            active={sortKey === "project"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th {...headerProps("consumido", "text-right py-2 px-4")}>
                                        Consumido
                                        <SortIndicator
                                            active={sortKey === "consumido"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th
                                        {...headerProps("presupuesto", "text-right py-2 px-4")}
                                    >
                                        Presupuesto
                                        <SortIndicator
                                            active={sortKey === "presupuesto"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th
                                        {...headerProps(
                                            "avance",
                                            "text-left py-2 px-4 min-w-[180px]",
                                        )}
                                    >
                                        Avance
                                        <SortIndicator
                                            active={sortKey === "avance"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th {...headerProps("fin", "text-left py-2 px-4")}>
                                        Fin estimado
                                        <SortIndicator
                                            active={sortKey === "fin"}
                                            dir={sortDir}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.slice(0, limit).map((p) => (
                                    <ProgressRow key={p.project_id} p={p} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {rows.length > limit && (
                        <div className="border-t border-slate-100 py-3 text-center">
                            <button
                                onClick={() => setLimit((n) => n + 15)}
                                className="text-xs text-[#002BAF] hover:underline"
                                data-testid="proj-progress-more"
                            >
                                Mostrar más ({rows.length - limit} restantes)
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function ProgressRow({ p }) {
    const pct = p.progress_pct;
    const over = p.is_over_budget;
    const noBudget = pct == null;
    const barPct = noBudget ? 0 : Math.min(100, pct);

    let barColor = "bg-emerald-500";
    let pctColor = "text-emerald-700";
    if (over) {
        barColor = "bg-rose-500";
        pctColor = "text-rose-700";
    } else if (pct != null && pct >= 90) {
        barColor = "bg-amber-500";
        pctColor = "text-amber-700";
    } else if (pct != null && pct >= 75) {
        barColor = "bg-amber-400";
        pctColor = "text-amber-700";
    }

    return (
        <tr
            className="border-b border-slate-100 hover:bg-slate-50"
            data-testid={`proj-progress-row-${p.project_id}`}
        >
            <td className="py-3 px-4">
                <div className="font-medium text-slate-900 truncate max-w-[280px]">
                    {p.project_name}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 font-mono">
                    {p.project_code && <span>{p.project_code}</span>}
                    <span className="text-slate-400">·</span>
                    <span className="truncate max-w-[180px]">{p.customer_name}</span>
                </div>
            </td>
            <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-800">
                {fmtEuro(p.spent_cost)}
                <div className="text-[10px] text-slate-500">
                    {p.spent_hours.toLocaleString("es-ES", {
                        maximumFractionDigits: 1,
                    })}{" "}
                    h
                </div>
            </td>
            <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-800">
                {p.planned_budget > 0 ? (
                    <>
                        {fmtEuro(p.planned_budget)}
                        <div className="text-[10px] text-slate-500">
                            {p.planned_hours.toLocaleString("es-ES", {
                                maximumFractionDigits: 1,
                            })}{" "}
                            h
                        </div>
                    </>
                ) : (
                    <span className="text-slate-400 italic text-xs">—</span>
                )}
            </td>
            <td className="py-3 px-4">
                {noBudget ? (
                    <span className="text-xs text-slate-400 italic">
                        Sin presupuesto definido
                    </span>
                ) : (
                    <div>
                        <div className="h-2 bg-slate-100 relative overflow-hidden">
                            <div
                                className={`absolute inset-y-0 left-0 ${barColor} transition-all`}
                                style={{ width: `${barPct}%` }}
                            />
                        </div>
                        <div
                            className={`text-[11px] mt-1 font-mono ${pctColor} flex items-center gap-1`}
                        >
                            {over && <Warning size={10} weight="fill" />}
                            {pct.toFixed(1)}%
                            {over && (
                                <span className="text-rose-700 font-semibold">
                                    · sobre presupuesto
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </td>
            <td className="py-3 px-4 text-xs">
                {p.estimated_end ? (
                    <div>
                        <div
                            className={`font-medium ${over ? "text-rose-700" : "text-slate-800"}`}
                            data-testid={`proj-est-end-${p.project_id}`}
                        >
                            {fmtDate(p.estimated_end)}
                        </div>
                        <div className="text-[10px] text-slate-500">
                            {over
                                ? "Última entrada"
                                : `Ritmo actual`}
                        </div>
                    </div>
                ) : (
                    <span className="text-slate-400 italic">
                        {p.spent_hours > 0 ? "Datos insuficientes" : "Sin actividad"}
                    </span>
                )}
            </td>
        </tr>
    );
}
